import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { BookingFilters } from "@/components/host/bookings/BookingFilters";
import { BookingsTable } from "@/components/host/bookings/BookingsTable";
import { BookingDetailsDialog } from "@/components/host/bookings/BookingDetailsDialog";

const HostBookingsPage = () => {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const { toast } = useToast();

  const { data: bookings, isLoading } = useQuery({
    queryKey: ["host-bookings", statusFilter, search],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      let query = supabase
        .from("bookings")
        .select(`
          *,
          property:properties(title),
          guest:profiles!bookings_user_id_fkey(full_name, phone_number),
          payment:transactions(amount, status, provider)
        `)
        .eq('properties.host_id', user.id);

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (search) {
        query = query.or(`guest.full_name.ilike.%${search}%,property.title.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const updateBookingStatus = async (bookingId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({ status: newStatus })
        .eq("id", bookingId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Booking ${newStatus} successfully`,
      });

      setSelectedBooking(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update booking status",
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Bookings Management</h1>
        <BookingFilters
          statusFilter={statusFilter}
          search={search}
          onStatusChange={setStatusFilter}
          onSearchChange={setSearch}
        />
      </div>

      <BookingsTable
        bookings={bookings || []}
        formatCurrency={formatCurrency}
        onViewDetails={setSelectedBooking}
      />

      <BookingDetailsDialog
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onUpdateStatus={updateBookingStatus}
      />
    </div>
  );
};

export default HostBookingsPage;